import api from './api';

export interface Brand {
  id: number;
  name: string;
}

export interface Model {
  id: number;
  name: string;
}

export interface Year {
  id: string;
  name: string;
}

export interface Price {
  CodigoFipe: string;
  brand_id: number;
  model_id: number;
  year_id: string;
  price: number;
  brand: string;
  model: string;
  year: string;
  fuel: string;
  reference_month: string;
  vehicleType: string
}

// API pública da FIPE
const FIPE_API_URL = 'https://parallelum.com.br/fipe/api/v1';
const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiJjMWY4MGE2OS1iNjkwLTRkNjEtOTNlYy0xYjQxNGM0ZmZjZGMiLCJlbWFpbCI6Imphb2ZlbHBlMTRAZ21haWwuY29tIiwiaWF0IjoxNzQxNzIzNzYxfQ.6u3a-KwOtwtVCet1SpzQVGlgY77HR1lesjNitCd8NZk';

const CACHE_EXPIRATION_TIME = 10 * 60 * 1000; // Tempo de expiração do cache em milissegundos (10 minutos)

const cache = {
  getCache: (key: string) => {
    const cachedData = localStorage.getItem(key);
    if (cachedData) {
      const parsedData = JSON.parse(cachedData);
      if (Date.now() - parsedData.timestamp < CACHE_EXPIRATION_TIME) {
        return parsedData.data; 
      }
    }
    return null; 
  },

  // Função para armazenar dados no cache
  setCache: (key: string, data: any) => {
    const cacheData = {
      timestamp: Date.now(),
      data,
    };
    localStorage.setItem(key, JSON.stringify(cacheData)); 
  },
};

const fipeService = {

  
  /**
   * Busca todas as marcas de veículos
   */
  getBrands: async (): Promise<Brand[]> => {
    const cacheKey = 'brands';
    const cachedBrands = cache.getCache(cacheKey); 

    if (cachedBrands) {
      return cachedBrands; 
    }

    try {
      const response = await fetch(`${FIPE_API_URL}/carros/marcas`, 
        {
        method: 'GET',
        headers: {
          'X-Subscription-Token': token,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Falha ao buscar marcas da API pública');
      }

      const data = await response.json();

      const brands: Brand[] = data.map((item: any) => ({
        id: parseInt(item.codigo),
        name: item.nome,
      }));

      // Armazenar os dados no cache para futuras consultas
      cache.setCache(cacheKey, brands);

      return brands;
    } catch (error) {
      console.error('Erro ao buscar marcas:', error);

      // Retorna algumas marcas default caso algo dê errado
      return [
        { id: 1, name: 'Volkswagen' },
        { id: 2, name: 'Chevrolet' },
        { id: 3, name: 'Fiat' },
        { id: 4, name: 'Ford' },
        { id: 5, name: 'Honda' },
        { id: 6, name: 'Hyundai' },
        { id: 7, name: 'Toyota' },
        { id: 21, name: 'Nissan' },
        { id: 25, name: 'Renault' },
        { id: 59, name: 'BMW' },
        { id: 41, name: 'Mercedes-Benz' },
      ];
    }
  },

  /**
   * Busca os modelos de uma marca específica
   * @param brandId ID da marca
   */
  getModels: async (brandId: number): Promise<Model[]> => {
    const cacheKey = `models_${brandId}`;
    const cachedModels = cache.getCache(cacheKey);

    if (cachedModels) {
      return cachedModels;
    }

    try {
      const response = await fetch(`${FIPE_API_URL}/carros/marcas/${brandId}/modelos`,
        {
        method: 'GET',
        headers: {
          'X-Subscription-Token': token,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Falha ao buscar modelos da API pública');
      }

      const data = await response.json();

      const models: Model[] = data.modelos.map((item: any) => ({
        id: parseInt(item.codigo),
        name: item.nome,
      }));

      cache.setCache(cacheKey, models);
      return models;
    } catch (error) {
      console.error('Erro ao buscar modelos para marca', brandId, error);
      return [];
    }
  },

  /**
   * Busca os anos disponíveis para um modelo específico
   * @param brandId ID da marca
   * @param modelId ID do modelo
   */
  getYears: async (brandId: number, modelId: number): Promise<Year[]> => {
    const cacheKey = `years_${brandId}_${modelId}`;
    const cachedYears = cache.getCache(cacheKey);

    if (cachedYears) {
      return cachedYears;
    }

    try {
      const response = await fetch(`${FIPE_API_URL}/carros/marcas/${brandId}/modelos/${modelId}/anos`,
        {
        method: 'GET',
        headers: {
          'X-Subscription-Token': token,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Falha ao buscar anos da API pública');
      }

      const data = await response.json();

      const years: Year[] = data.map((item: any) => ({
        id: item.codigo,
        name: item.nome,
      }));

      cache.setCache(cacheKey, years);
      return years;
    } catch (error) {
      console.error('Erro ao buscar anos para marca/modelo', brandId, modelId, error);
      return [];
    }
  },

  /**
   * Busca o preço de um veículo específico
   * @param brandId ID da marca
   * @param modelId ID do modelo
   * @param yearId ID do ano
   */
  getPrice: async (brandId: number, modelId: number, yearId: string): Promise<Price> => {
    const cacheKey = `price_${brandId}_${modelId}_${yearId}`;
    const cachedPrice = cache.getCache(cacheKey);

    if (cachedPrice) {
      return cachedPrice;
    }

    try {
      const response = await fetch(`${FIPE_API_URL}/carros/marcas/${brandId}/modelos/${modelId}/anos/${yearId}`,
        {
        method: 'GET',
        headers: {
          'X-Subscription-Token': token,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Falha ao buscar preço da API pública`);
      }

      const data = await response.json();

      const priceStr = data.Valor.replace('R$ ', '').replace('.', '').replace(',', '.');
      const price = parseFloat(priceStr);

      const priceData: Price = {
        brand_id: brandId,
        model_id: modelId,
        year_id: yearId,
        price,
        brand: data.Marca,
        model: data.Modelo,
        year: data.AnoModelo.toString(),
        fuel: data.Combustivel,
        reference_month: data.MesReferencia,
        CodigoFipe:data.CodigoFipe,
        vehicleType: data.TipoVeiculo

      };

      cache.setCache(cacheKey, priceData);
      return priceData;
    } catch (error) {
      console.error('Erro ao buscar preço para marca/modelo/ano', brandId, modelId, yearId, error);

      return {
        brand_id: brandId,
        model_id: modelId,
        year_id: yearId,
        price: 0,
        brand: '',
        model: '',
        year: '',
        fuel: '',
        reference_month: '',
        CodigoFipe:'',
        vehicleType:''
      };
    }
  },
  getVehicleHistory: async (vehicleType: any, fipeCode: string, yearId: any): Promise<any> => {
    // Map vehicle type numbers to the appropriate string (cars, motorcycles, trucks)
    const vehicleTypeMap: { [key: number]: string } = {
      1: 'cars',
      2: 'motos',
      3: 'caminhoes',
    };

    // Map the numeric vehicleType to the corresponding string
    const typeString = vehicleTypeMap[vehicleType];

    // If the vehicleType is invalid, throw an error
    if (!typeString) {
      throw new Error('Tipo de veículo inválido. Use 1 para carros, 2 para motos e 3 para caminhões.');
    }

    const cacheKey = `history_${typeString}_${fipeCode}_${yearId}`;
    const cachedHistory = cache.getCache(cacheKey);

    if (cachedHistory) {
      return cachedHistory;
    }

    try {
      const response = await fetch(`https://fipe.parallelum.com.br/api/v2/${typeString}/${fipeCode}/years/${yearId}/history`, {
        method: 'GET',
        headers: {
          'X-Subscription-Token': token,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Falha ao buscar histórico da API pública');
      }

      const data = await response.json();

      // Cache the response for future use
      cache.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Erro ao buscar histórico para o veículo', vehicleType, fipeCode, yearId, error);
      return null;
    }
  },

  
};





export default fipeService;
